import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowLeft, ArrowUp, ClipboardPaste, Eye, History, Lock, Plus, Save, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import SurveiTempelExcel from "@/components/SurveiTempelExcel";
import SurveiKunciVersiDialog from "@/components/SurveiKunciVersiDialog";
import SurveiRiwayatVersi from "@/components/SurveiRiwayatVersi";
import SurveiPratinjauFormulir from "@/components/SurveiPratinjauFormulir";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    barisBaru, butirPublikDariEditor, drafKeEditor, editorKeKirim, galatServer, hapusBaris, jumlahBolehTb, PESAN_KONFLIK, periksaEditor,
    pindahBaris, ringkasPerDimensi, samaIsi, sisipSetelah, terapkanTempel, TEKS_BUTIR_MAKS, ubahBaris,
} from "@/lib/surveiEditor";

const KELAS_PILIH = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm";

// Editor DRAF versi butir (Super Admin). Isi di layar baru tersimpan saat "Simpan draf" (PUT dgn rev; 409 = konflik ->
// muat ulang, tidak pernah menimpa diam-diam). Urutan baris = nomor 1..N. Kunci = permanen (dialog konfirmasi).
export default function SurveiEditorVersi({ versiId, onKembali, onBerubah }) {
    const [doc, setDoc] = useState(null);
    const [asli, setAsli] = useState(null);
    const [isi, setIsi] = useState(null);
    const [galatSrv, setGalatSrv] = useState(null);
    const [konflik, setKonflik] = useState("");
    const [sibuk, setSibuk] = useState(false);
    const [dialog, setDialog] = useState(""); // "tempel" | "kunci" | "riwayat" | "pratinjau"

    const terima = useCallback((d) => {
        const e = drafKeEditor(d);
        setDoc(d); setAsli(e); setIsi(e); setGalatSrv(null); setKonflik("");
    }, []);
    const muat = useCallback(() => api.get(`/survei/versi-butir/${versiId}`)
        .then((r) => terima(r.data))
        .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail))), [versiId, terima]);
    useEffect(() => { muat(); }, [muat]);

    const belumDisimpan = !!(isi && asli && !samaIsi(isi, asli));
    useEffect(() => {
        if (!belumDisimpan) return undefined;
        const peringatkan = (ev) => { ev.preventDefault(); ev.returnValue = ""; };
        window.addEventListener("beforeunload", peringatkan);
        return () => window.removeEventListener("beforeunload", peringatkan);
    }, [belumDisimpan]);

    const cek = useMemo(() => (isi ? periksaEditor(isi) : null), [isi]);
    const namaDimensi = useMemo(() => Object.fromEntries((isi?.dimensi || []).map((d) => [d.kode, d.nama])), [isi]);
    if (!doc || !isi) return <p className="py-6 text-sm text-slate-500">Memuat…</p>;

    const ubah = (perubahan) => { setIsi((x) => ({ ...x, ...perubahan })); setGalatSrv(null); };
    const ubahButir = (fn) => ubah({ butir: fn(isi.butir) });
    const galatBaris = (i) => [...(cek.baris[i] || []), ...((galatSrv?.baris || {})[i] || [])];
    const draf = doc.status === "draf" && !doc.terkunci;

    const tanganiGalat = (e) => {
        const status = e.response?.status;
        const detail = e.response?.data?.detail;
        if (status === 409) setKonflik(typeof detail === "string" ? `${detail} ${PESAN_KONFLIK}` : PESAN_KONFLIK);
        else if (status === 400 && detail?.galat) { setGalatSrv(galatServer(detail.galat)); toast.error(detail.pesan); }
        else toast.error(formatApiErrorDetail(detail));
    };
    const simpan = async () => {
        setSibuk(true);
        try {
            const res = await api.put(`/survei/versi-butir/${versiId}`, editorKeKirim(isi, doc.rev));
            terima({ ...res.data, terkunci: false });
            toast.success("Draf disimpan");
            onBerubah();
        } catch (e) { tanganiGalat(e); } finally { setSibuk(false); }
    };
    const kunci = async () => {
        setDialog("");
        try {
            await api.post(`/survei/versi-butir/${versiId}/kunci`, { rev: doc.rev });
            toast.success("Versi dikunci. Sekarang bisa dipilih untuk periode baru.");
            onBerubah();
            onKembali();
        } catch (e) { tanganiGalat(e); }
    };
    const pulihkan = async (rev) => {
        setDialog("");
        try {
            const res = await api.post(`/survei/versi-butir/${versiId}/riwayat/${rev}/pulihkan`, { rev: doc.rev });
            terima({ ...res.data, terkunci: false });
            toast.success(`Isi revisi ${rev} dipulihkan`);
            onBerubah();
        } catch (e) { tanganiGalat(e); }
    };
    const kembali = () => {
        if (belumDisimpan && !window.confirm("Ada perubahan yang belum disimpan. Tinggalkan editor?")) return;
        onKembali();
    };
    const jumlahGalat = cek.jumlah + (galatSrv ? Object.keys(galatSrv.baris).length + galatSrv.umum.length : 0);

    return (
        <div className="space-y-4" data-testid="survei-editor">
            <Card className="space-y-3 p-4">
                <div className="flex flex-wrap items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={kembali} data-testid="survei-editor-kembali"><ArrowLeft className="mr-1 h-4 w-4" /> Daftar versi</Button>
                    <Badge variant="outline">{draf ? "Draf" : "Terkunci"}</Badge>
                    <span className="text-xs text-slate-500" data-testid="survei-editor-rev">Rev {doc.rev}</span>
                    {belumDisimpan && <Badge className="bg-amber-500 text-white" data-testid="survei-editor-belum-disimpan">Belum disimpan</Badge>}
                </div>
                <div className="grid gap-3 md:grid-cols-[1fr_14rem]">
                    <label className="space-y-1 text-sm">
                        <span className="font-semibold text-slate-700">Nama versi</span>
                        <Input value={isi.nama} maxLength={100} onChange={(e) => ubah({ nama: e.target.value })} data-testid="survei-editor-nama" />
                    </label>
                    <label className="space-y-1 text-sm">
                        <span className="font-semibold text-slate-700">Label (huruf/angka/-/_)</span>
                        <Input value={isi.label} maxLength={30} onChange={(e) => ubah({ label: e.target.value })} data-testid="survei-editor-label" />
                    </label>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button onClick={simpan} disabled={sibuk || !belumDisimpan || !!konflik} className="bg-[#00B9AD] text-[#0F2E30] hover:bg-[#60C0D0]" data-testid="survei-editor-simpan">
                        <Save className="mr-1 h-4 w-4" /> Simpan draf
                    </Button>
                    <Button variant="outline" onClick={() => setDialog("tempel")} data-testid="survei-editor-tempel"><ClipboardPaste className="mr-1 h-4 w-4" /> Tempel dari Excel</Button>
                    <Button variant="outline" onClick={() => setDialog("pratinjau")} data-testid="survei-editor-pratinjau"><Eye className="mr-1 h-4 w-4" /> Pratinjau formulir</Button>
                    <Button variant="outline" onClick={() => setDialog("riwayat")} data-testid="survei-editor-riwayat"><History className="mr-1 h-4 w-4" /> Riwayat</Button>
                    <Button variant="outline" onClick={() => setDialog("kunci")} disabled={belumDisimpan || jumlahGalat > 0 || !!konflik}
                            title={belumDisimpan ? "Simpan draf dulu" : undefined} data-testid="survei-editor-kunci">
                        <Lock className="mr-1 h-4 w-4" /> Kunci versi
                    </Button>
                </div>
                {konflik && (
                    <div role="alert" className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200" data-testid="survei-editor-konflik">
                        <span>{konflik}</span>
                        <Button size="sm" variant="outline" onClick={muat} data-testid="survei-editor-muat-ulang">Muat ulang</Button>
                    </div>
                )}
            </Card>

            <Card className="space-y-2 p-4" data-testid="survei-editor-ringkasan">
                <div className="text-sm"><span className="font-semibold">{isi.butir.length} butir</span>
                    <span className="ml-2 text-slate-600" data-testid="survei-editor-jumlah-tb">
                        · {isi.opsi_tb.aktif ? `${jumlahBolehTb(isi)} boleh ${isi.opsi_tb.label || "TB"}` : "Opsi TB nonaktif"}
                    </span>
                    {jumlahGalat > 0 && <span className="ml-2 text-rose-700" data-testid="survei-editor-jumlah-galat">· {jumlahGalat} galat</span>}
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                    {ringkasPerDimensi(isi).map((d) => (
                        <span key={d.kode} data-testid={`survei-editor-dim-${d.kode}`}
                              className={`rounded border px-2 py-1 ${d.jumlah ? "border-slate-200 text-slate-600" : "border-rose-400 bg-rose-50 font-semibold text-rose-700 dark:bg-rose-950"}`}>
                            {d.kode}: {d.jumlah}
                        </span>
                    ))}
                </div>
                {[...cek.umum, ...(galatSrv?.umum || [])].length > 0 && (
                    <ul className="list-disc pl-5 text-xs text-rose-700" data-testid="survei-editor-galat-umum">
                        {[...new Set([...cek.umum, ...(galatSrv?.umum || [])])].map((g) => <li key={g}>{g}</li>)}
                    </ul>
                )}
            </Card>

            <Card className="space-y-3 p-4">
                <h3 className="text-sm font-semibold text-slate-800">Dimensi (kode tetap)</h3>
                <div className="space-y-2">
                    {isi.dimensi.map((d, i) => (
                        <div key={d.kode} className="grid grid-cols-[3rem_1fr] items-center gap-2 md:grid-cols-[3rem_1fr_1fr]">
                            <span className="text-sm font-semibold text-slate-600">{d.kode}</span>
                            <Input aria-label={`Nama dimensi ${d.kode}`} value={d.nama} onChange={(e) => ubah({ dimensi: isi.dimensi.map((x, j) => (j === i ? { ...x, nama: e.target.value } : x)) })} />
                            <Input aria-label={`Nama Excel dimensi ${d.kode}`} className="col-start-2 md:col-start-auto" value={d.nama_excel}
                                   onChange={(e) => ubah({ dimensi: isi.dimensi.map((x, j) => (j === i ? { ...x, nama_excel: e.target.value } : x)) })} />
                        </div>
                    ))}
                </div>
            </Card>

            <Card className="p-4">
                <h3 className="mb-2 text-sm font-semibold text-slate-800">Butir (urutan baris = nomor)</h3>
                <div className="divide-y divide-slate-200" data-testid="survei-editor-butir">
                    {isi.butir.map((b, i) => {
                        const galat = galatBaris(i);
                        return (
                            <div key={b.kunci} className={`grid grid-cols-[2.5rem_1fr] gap-2 py-2 md:grid-cols-[2.5rem_13rem_1fr_auto] ${galat.length ? "bg-rose-50/60 dark:bg-rose-950/40" : ""}`} data-testid={`survei-editor-baris-${i + 1}`}>
                                <span className="pt-2 text-center text-sm tabular-nums text-slate-600">{i + 1}</span>
                                <select aria-label={`Dimensi butir ${i + 1}`} className={KELAS_PILIH} value={b.dimensi_kode}
                                        onChange={(e) => ubahButir((x) => ubahBaris(x, i, { dimensi_kode: e.target.value }))}>
                                    {isi.dimensi.map((d) => <option key={d.kode} value={d.kode}>{d.kode} · {d.nama}</option>)}
                                </select>
                                <div className="col-start-2 space-y-1 md:col-start-auto">
                                    <Textarea aria-label={`Teks butir ${i + 1}`} rows={2} value={b.teks} maxLength={TEKS_BUTIR_MAKS + 100}
                                              onChange={(e) => ubahButir((x) => ubahBaris(x, i, { teks: e.target.value }))} />
                                    {galat.map((g) => <p key={g} className="text-xs text-rose-700">{g}</p>)}
                                </div>
                                <div className="col-start-2 flex flex-wrap items-center gap-1 md:col-start-auto">
                                    <label className="mr-2 flex items-center gap-1 text-xs">
                                        <input type="checkbox" checked={b.kalimat_negatif} aria-label={`Kalimat negatif butir ${i + 1}`}
                                               onChange={(e) => ubahButir((x) => ubahBaris(x, i, { kalimat_negatif: e.target.checked }))} />
                                        Negatif
                                    </label>
                                    <label className="mr-2 flex items-center gap-1 text-xs">
                                        <input type="checkbox" checked={b.boleh_tb !== false} aria-label={`Boleh TB butir ${i + 1}`}
                                               disabled={!isi.opsi_tb.aktif}
                                               onChange={(e) => ubahButir((x) => ubahBaris(x, i, { boleh_tb: e.target.checked }))} />
                                        Boleh TB
                                    </label>
                                    <Button size="icon" variant="ghost" aria-label={`Naikkan butir ${i + 1}`} disabled={i === 0} onClick={() => ubahButir((x) => pindahBaris(x, i, -1))}><ArrowUp className="h-4 w-4" /></Button>
                                    <Button size="icon" variant="ghost" aria-label={`Turunkan butir ${i + 1}`} disabled={i === isi.butir.length - 1} onClick={() => ubahButir((x) => pindahBaris(x, i, 1))}><ArrowDown className="h-4 w-4" /></Button>
                                    <Button size="icon" variant="ghost" aria-label={`Tambah butir di bawah butir ${i + 1}`} onClick={() => ubahButir((x) => sisipSetelah(x, i, barisBaru(b.dimensi_kode)))}><Plus className="h-4 w-4" /></Button>
                                    <Button size="icon" variant="ghost" className="text-rose-600" aria-label={`Hapus butir ${i + 1}`} onClick={() => ubahButir((x) => hapusBaris(x, i))}><Trash2 className="h-4 w-4" /></Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <Button variant="outline" className="mt-3" onClick={() => ubahButir((x) => [...x, barisBaru(isi.butir.at(-1)?.dimensi_kode || isi.dimensi[0].kode)])} data-testid="survei-editor-tambah-akhir">
                    <Plus className="mr-1 h-4 w-4" /> Tambah butir di akhir
                </Button>
            </Card>

            <Card className="space-y-3 p-4">
                <h3 className="text-sm font-semibold text-slate-800">Isian terbuka (tidak wajib diisi peserta)</h3>
                {isi.terbuka.map((t, i) => (
                    <label key={t.kode} className="block space-y-1 text-sm">
                        <span className="text-slate-600">{t.kode === "positif" ? "Hal positif" : "Hal negatif"}</span>
                        <Textarea rows={2} value={t.label} data-testid={`survei-editor-terbuka-${t.kode}`}
                                  onChange={(e) => ubah({ terbuka: isi.terbuka.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} />
                    </label>
                ))}
                <p className="text-xs text-slate-500">Skala {doc.skala?.min}–{doc.skala?.max}; Puas bila rata-rata dimensi ≥ {doc.ambang_puas} (tetap, tidak bisa diubah).</p>
                <fieldset className="space-y-2 rounded-lg border border-slate-200 p-3" data-testid="survei-editor-opsi-tb">
                    <legend className="px-1 text-sm font-semibold text-slate-800">Opsi TB (Tidak Berlaku)</legend>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={isi.opsi_tb.aktif} data-testid="survei-editor-tb-aktif"
                               onChange={(e) => ubah({ opsi_tb: { ...isi.opsi_tb, aktif: e.target.checked } })} />
                        Peserta boleh memilih TB pada butir yang ditandai "Boleh TB"
                    </label>
                    <div className="grid gap-2 md:grid-cols-[8rem_1fr]">
                        <label className="space-y-1 text-sm">
                            <span className="text-slate-600">Label (maks. 8)</span>
                            <Input value={isi.opsi_tb.label} maxLength={8} disabled={!isi.opsi_tb.aktif} data-testid="survei-editor-tb-label"
                                   onChange={(e) => ubah({ opsi_tb: { ...isi.opsi_tb, label: e.target.value } })} />
                        </label>
                        <label className="space-y-1 text-sm">
                            <span className="text-slate-600">Keterangan (dibacakan pembaca layar)</span>
                            <Input value={isi.opsi_tb.keterangan} maxLength={200} disabled={!isi.opsi_tb.aktif} data-testid="survei-editor-tb-keterangan"
                                   onChange={(e) => ubah({ opsi_tb: { ...isi.opsi_tb, keterangan: e.target.value } })} />
                        </label>
                    </div>
                    <p className="text-xs text-slate-500">TB diabaikan dalam rata-rata; dimensi yang seluruh butirnya TB tidak dihitung, responden tetap masuk pembagi % Puas.</p>
                </fieldset>
                <label className="block space-y-1 text-sm">
                    <span className="text-slate-600">Catatan tambahan untuk catatan metode Excel (opsional)</span>
                    <Textarea rows={2} value={isi.catatan_tambahan} onChange={(e) => ubah({ catatan_tambahan: e.target.value })} data-testid="survei-editor-catatan" />
                </label>
            </Card>

            <SurveiTempelExcel open={dialog === "tempel"} versiId={versiId} butirSekarang={isi.butir} namaDimensi={namaDimensi}
                               onTutup={() => setDialog("")}
                               onTerapkan={(hasil, mode) => { ubahButir((x) => terapkanTempel(x, hasil, mode)); setDialog(""); }} />
            <SurveiKunciVersiDialog open={dialog === "kunci"} isi={asli} label={doc.label} onBatal={() => setDialog("")} onKunci={kunci} />
            <SurveiRiwayatVersi open={dialog === "riwayat"} versiId={versiId} revSekarang={doc.rev} bolehPulihkan={draf && !belumDisimpan}
                                onTutup={() => setDialog("")} onPulihkan={pulihkan} />
            {dialog === "pratinjau" && <SurveiPratinjauFormulir butir={butirPublikDariEditor(isi, doc)} onTutup={() => setDialog("")} />}
        </div>
    );
}
